import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Phone, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mock';

const AutoQuotePage = () => {
  const { language } = useLanguage();
  const scriptLoaded = useRef(false);

  useEffect(() => {
    // Load AgencyStream widget script only once
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.src = 'https://api.agencystream.com/widget/auto-quote-widget.js';
      script.async = true;
      script.onload = () => {
        console.log('AgencyStream widget script loaded successfully');
      };
      script.onerror = () => {
        console.error('Failed to load AgencyStream widget script');
      };
      document.body.appendChild(script);
      scriptLoaded.current = true;

      return () => {
        // Cleanup: remove script on unmount
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, []);

  const content = {
    en: {
      title: 'Get Your Auto Insurance Quote',
      subtitle: 'Compare rates from multiple carriers in minutes',
      description: 'Fill out the form below to receive personalized auto insurance quotes. Our system connects with multiple carriers to find you the best coverage at competitive rates.',
      backButton: 'Back to Home',
      needHelp: 'Need Help?',
      callUs: 'Call Us',
      whatsappUs: 'WhatsApp Us',
      note: 'Note: This quote tool is provided by AgencyStream. All information is securely transmitted.',
      disclaimer: '⚠️ Do not share sensitive personal information beyond what is required for the quote.'
    },
    es: {
      title: 'Obtenga su Cotización de Seguro de Auto',
      subtitle: 'Compare tarifas de múltiples aseguradoras en minutos',
      description: 'Complete el formulario a continuación para recibir cotizaciones personalizadas de seguro de auto. Nuestro sistema se conecta con múltiples aseguradoras para encontrarle la mejor cobertura a tarifas competitivas.',
      backButton: 'Volver al Inicio',
      needHelp: '¿Necesita Ayuda?',
      callUs: 'Llámenos',
      whatsappUs: 'WhatsApp',
      note: 'Nota: Esta herramienta de cotización es proporcionada por AgencyStream. Toda la información se transmite de forma segura.',
      disclaimer: '⚠️ No comparta información personal sensible más allá de lo requerido para la cotización.'
    }
  };

  const text = content[language];
  const whatsappUrl = `https://wa.me/${companyInfo.phoneWhatsApp}?text=${encodeURIComponent(
    language === 'en' 
      ? 'Hi, I need help with an auto insurance quote.' 
      : 'Hola, necesito ayuda con una cotización de seguro de auto.'
  )}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/" 
              className="inline-flex items-center text-white hover:text-red-100 mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {text.backButton}
            </Link>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {text.title}
            </h1>
            <p className="text-xl md:text-2xl text-red-100 mb-4">
              {text.subtitle}
            </p>
            <p className="text-lg text-red-50">
              {text.description}
            </p>
          </div>
        </div>
      </section>

      {/* Widget Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Info Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg mb-8">
              <p className="text-sm text-blue-900">
                <strong>{text.note}</strong>
              </p>
              <p className="text-xs text-blue-800 mt-2">
                {text.disclaimer}
              </p>
            </div>

            {/* AgencyStream Widget */}
            <div className="bg-white rounded-lg shadow-xl p-8">
              <auto-quote-widget 
                agency-id="94788"
              ></auto-quote-widget>
            </div>

            {/* Help Section */}
            <div className="mt-12 bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {text.needHelp}
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
                  aria-label={`${text.callUs}: ${companyInfo.phone}`}
                >
                  <Phone className="w-6 h-6" />
                  {text.callUs}: {companyInfo.phone}
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-lg focus:outline-none focus:ring-4 focus:ring-green-300"
                  aria-label={text.whatsappUs}
                >
                  <MessageCircle className="w-6 h-6" />
                  {text.whatsappUs}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutoQuotePage;
